import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Model, { attr, type HasMany, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import type { Hotelinfo } from 'frontend-utils/models';
import type { HotelInfoResponse } from 'lumen/components/settings/competitors/input-autocomplete-competitor';
import type { BenchmarkInsightCompetitorsCompetitor } from 'lumen/models';
import dirtyRelationship, {
  type DirtyRelationshipModel,
} from 'lumen/utils/decorators/dirty-relationship';

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
interface BenchmarkInsightCompetitorsCompset
  extends DirtyRelationshipModel, Model {}

@dirtyRelationship()
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class BenchmarkInsightCompetitorsCompset extends Model {
  [Type] = 'benchmark-insight/competitors/compset' as const;

  @hasMany('benchmark-insight/competitors/competitor', {
    async: false,
    inverse: null,
  })
  declare compset: HasMany<BenchmarkInsightCompetitorsCompetitor>;

  @attr('string') declare cantChangeUntil: string | null;
  @attr('boolean') declare isCompsetValid: boolean;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  MAX_COMPETITORS = 10;
  MIN_COMPETITORS = 1;

  get competitors(): BenchmarkInsightCompetitorsCompetitor[] {
    return [...this.compset];
  }

  get hasChanges(): boolean {
    return this.hasDirtyRelationships;
  }

  get hasReachedMaxCompetitors(): boolean {
    return this.competitors.length >= this.MAX_COMPETITORS;
  }

  get isAmountOfCompetitorsValid(): boolean {
    return (
      this.compset.length >= this.MIN_COMPETITORS &&
      this.compset.length <= this.MAX_COMPETITORS
    );
  }

  get isCompsetEditable(): boolean {
    if (!this.cantChangeUntil) {
      return true;
    }

    const cantChangeUntilDate = dayjs.utc(this.cantChangeUntil);
    const today = dayjs.utc();

    return (
      today.isSame(cantChangeUntilDate) || today.isAfter(cantChangeUntilDate)
    );
  }

  mapHotelinfoToCompetitor(
    hotelinfo: Hotelinfo | HotelInfoResponse,
  ): BenchmarkInsightCompetitorsCompetitor {
    const id = 'id' in hotelinfo ? hotelinfo.id : hotelinfo.hotelinfo_id;

    if (id) {
      const existingRecord =
        this.store.peekRecord<BenchmarkInsightCompetitorsCompetitor>(
          'benchmark-insight/competitors/competitor',
          id,
        );

      if (existingRecord) {
        return existingRecord;
      }
    }

    return this.store.createRecord<BenchmarkInsightCompetitorsCompetitor>(
      'benchmark-insight/competitors/competitor',
      {
        // @ts-expect-error: Type 'string | number | null' is not assignable to type 'string | null | undefined'
        id,
        name: hotelinfo.name,
        // @ts-expect-error: Type 'number | null' is not assignable to type 'number | undefined'.
        stars: hotelinfo.stars,
        latitude: hotelinfo.latitude,
        longitude: hotelinfo.longitude,
      },
    );
  }

  @action addCompetitor(hotelinfo: Hotelinfo): void {
    const competitor = this.mapHotelinfoToCompetitor(hotelinfo);

    this.compset.push(competitor);
  }

  @action copyRICompset(): void {
    const riCompset = this.hotelAndMarketService.primaryCompset;

    if (!riCompset) {
      return;
    }

    const { competitors } = riCompset;

    competitors.forEach((competitor) => {
      const competitorRecord = this.mapHotelinfoToCompetitor(competitor);

      this.compset.push(competitorRecord);
    });
  }

  @action deleteCompetitor(
    competitor: BenchmarkInsightCompetitorsCompetitor,
  ): void {
    const index = this.compset.findIndex(({ id }) => id === competitor.id);

    if (index === -1) {
      return;
    }

    this.compset.splice(index, 1);
  }
}

export default BenchmarkInsightCompetitorsCompset;
